import type { AppLocale } from '@/i18n/messages'
import { streamMessages } from '@/i18n/stream'

export interface StreamHudData {
  sessionId: string | null
  status: string
  statusText: string
  elapsed: string
  level: string
  expRate: string
  expRateValue?: number | null
  expPercent: string
  expRatio: number
  eta: string
  expGained: string
  expGainedLabel: string
  fullDuration: string
  mesosRate: string
  mesosNet: string
  mesosStart: string
  mesosBalanceChange: string
  hpPercent: string
  hpRatio: number
  hpRate: string
  mpPercent: string
  mpRatio: number
  mpRate: string
  map: string
  enabled: { mesos: boolean; hp: boolean; mp: boolean; mapName: boolean }
  levelUpId?: string
}

export const STREAM_ASSETS = Object.freeze({
  exp: '/stream-assets/exp.png', mesos: '/stream-assets/mesos.png',
  hp: '/stream-assets/hp.png', mp: '/stream-assets/mp.png', mushroom: '/stream-assets/mushroom.png',
})

export type StreamShape = 'classic' | 'strip' | 'portrait'
export function resolveStreamShape(width: number, height: number): StreamShape {
  if (width >= 320 && width / height >= 2.5) return 'strip'
  if (height / width >= 1.5) return 'portrait'
  return 'classic'
}

/** Measure text as well as boxes: fixed grids can conceal clipped unit suffixes. */
export function streamContentFits(root: HTMLElement): boolean {
  const bounds = root.getBoundingClientRect()
  if (!bounds.width || !bounds.height) return true
  const range = root.ownerDocument.createRange()
  for (const node of root.querySelectorAll<HTMLElement>('[data-stream-measure]')) {
    if (!node.getClientRects().length) continue
    const box = node.getBoundingClientRect()
    if (box.left < bounds.left - 1 || box.right > bounds.right + 1 || box.top < bounds.top - 1 || box.bottom > bounds.bottom + 1) return false
    // Artwork owns a static footprint; moving sprites must not change detail tiers.
    if (node.hasAttribute('data-stream-artwork')) continue
    // Ancestors also inherit sprite overflow; their text is checked in its own boxes.
    if (!node.querySelector('[data-stream-artwork]') && node.clientWidth && node.scrollWidth > node.clientWidth + 1) return false
    for (const child of node.childNodes) {
      if (child.nodeType !== 3 || !child.textContent?.trim()) continue
      range.selectNodeContents(child)
      const text = range.getBoundingClientRect()
      if (text.left < bounds.left - 1 || text.right > bounds.right + 1 || text.top < bounds.top - 1 || text.bottom > bounds.bottom + 1) return false
    }
  }
  return true
}

export function fitStreamDetail(root: HTMLElement, fits = streamContentFits, preview = false): number {
  // Restore the theme before measuring, including when a larger window returns.
  root.dataset.artwork = 'visible'
  for (let tier = 5; tier >= 2; tier--) {
    root.dataset.detail = String(tier)
    if (fits(root)) return tier
  }
  // Preview cards showcase the theme: scale the core composition instead of removing it.
  if (preview) return 2
  // Try core data with its companion first; remove artwork before core metrics.
  root.dataset.artwork = 'hidden'
  for (let tier = 2; tier >= 0; tier--) {
    root.dataset.detail = String(tier)
    if (fits(root)) return tier
  }
  return 0
}

export function streamPreviewData(locale: AppLocale): StreamHudData {
  const en = locale === 'en'
  const compact = (value: number) => new Intl.NumberFormat(locale, { notation: 'compact', maximumFractionDigits: 1 }).format(value)
  return {
    sessionId: 'preview', status: 'running', statusText: streamMessages[locale].recording, elapsed: '01:47:37', level: '75',
    expRate: compact(304400), expRateValue: 304400, expPercent: '44.15%', expRatio: .4415, eta: en ? '4h 57m' : '4小时57分',
    expGained: `+${compact(68200)}`, expGainedLabel: streamMessages[locale].gained, fullDuration: en ? '8h 51m' : '8小时51分',
    mesosRate: compact(213200), mesosNet: `+${compact(47600)}`, mesosStart: compact(4336560), mesosBalanceChange: `+${compact(47600)}`,
    hpPercent: '53%', hpRatio: .53, hpRate: compact(21000), mpPercent: '49%', mpRatio: .49, mpRate: compact(45000),
    map: en ? 'Victoria Island / Forest of Wisdom' : '金银岛 / 智慧森林', enabled: { mesos: true, hp: true, mp: true, mapName: true },
  }
}
